/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-07-01 20:34:47
 * @LastEditors: Teemo Ding
 * @LastEditTime: 2022-06-29 11:46:50
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCurrency/index.tsx
 */
import { SectionWrapper } from '@/src/components';
import { Checkbox, Select } from '@/src/components/Antd';
import ItemWrapper from '@/src/components/Layouts/Item/Item';
import Link from '@/src/components/Link/Link';
import NoPermission from '@/src/components/NoPermission';
import GoBackAndBreadCrumb from '@/src/layout/EditorPageLayout/GoBackAndBreadCrumb/GoBackAndBreadCrumb';
import TopRightBtnsGroups from '@/src/layout/EditorPageLayout/TopRightBtnsGroups/TopRightBtnsGroups';
import useBreadCrumbData from '@/src/pages/SalesCannelVersion/Hooks/useBreadCrumbData';
import { SalesChannelModeAccessControlHOC } from 'src/constrains/SalesChannelModeAccessControl';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { Icon } from '@/src/tools/icon';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { ConfigProvider, Popover } from 'antd';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import RouteBlock from 'src/constrains/RouterBlock/RouterBlock';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import {
  // Cnversion,
  // DecimalSeparator,
  DisplayFormat,
  // ThousandsSeparator,
  conversionOptions,
  decimalPositionOptions,
  ruleStartPosition,
} from './constant';
import OtherAvailableCurrencyTable from './components/OtherAvailableCurrency';
import CurrencyConfigSection from './components/CurrencyConfigSection';
import { useSalesChannelIsMiniProgram } from 'src/constrains/SalesChannelHOC/useSalesChannel';
import useService from './service';
import styles from './style.module.less';

const permissions = (uuid: string) => {
  const view_currency_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCurrency,
        operation: EnumOperation.view_currency_setting,
        uuid,
      }),
    ],
  });
  const edit_currency_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCurrency,
        operation: EnumOperation.edit_currency_settings,
        uuid,
      }),
    ],
  });

  return {
    view_currency_setting,
    edit_currency_settings,
  };
};

function SalesChannelCurrency() {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const isMini = useSalesChannelIsMiniProgram();
  const {
    navigateBack,
    handleSubmit,
    handleDiscard,
    handleChangePropsViaEvent,
    handleChangePropsViaValue,
    handleChangeCurrency,
    handleOnAddOtherCurrency,
    handleOnDeleteOtherCurrency,
    handleChangeShowSecond,
    handleChangeSecondCurrency,
    handleChangeDecimalPosition,
    getOptions,
    //reportingCurrency,
    currencyConfig,
    saveDisabled,
    salesChannelUuid,
    secondCuSymbol,
    nonEditable,
    primaryCurrencyConfig,
  } = useService(permissions);
  const { breadcrumbData } = useBreadCrumbData({
    lastSecond: getI18n('general.sider.settings'),
    lastOne: getI18n('general.currency'),
  });
  const { view_currency_setting, edit_currency_settings } =
    permissions(salesChannelUuid);

  const labelify = (arr: Array<string>) =>
    (arr || []).map((a) => ({ label: a, value: a }));

  const sectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const formatExample = (type: DisplayFormat, symbol: string) => {
    switch (type) {
      case DisplayFormat.PREFIX_AMOUNT:
        return `${symbol} 1,245.00`;
      case DisplayFormat.AMOUNT_SUFFIX:
        return `1,245.00 ${symbol}`;
      default:
        return;
    }
  };

  const renderRoundingExample = (currencyConfig) => {
    const { roundingRule, decimalPosition } = currencyConfig;

    const position = ruleStartPosition(roundingRule);
    return `${replaceI18nVariables(
      getI18n('salesChannelCurrency.index.roundingof'),
      'HKD',
      '1,245.7892',
      'HKD',
    )}
    ${(
      Math.floor(1245.7892 * Math.pow(10, position)) / Math.pow(10, position)
    ).toFixed(decimalPosition)}`;
  };

  const renderTruncatingExample = (currencyConfig) => {
    const { truncateRule, decimalPosition } = currencyConfig;
    const position = ruleStartPosition(truncateRule) || 0;
    return `   ${replaceI18nVariables(
      getI18n('salesChannelCurrency.index.roundingof'),
      'CNY',
      '1,245.7892',
      'CNY',
    )}
    ${(
      Math.trunc(1245.7892 * Math.pow(10, position)) / Math.pow(10, position)
    ).toFixed(decimalPosition)}`;
  };

  // Add Avaliable Currency 可选列表筛选
  const existingCodes = new Set(
    (currencyConfig || []).filter((c) => !c.deleted).map((c) => c.currencyCode),
  );
  const currencyOptionExpectSelected = getOptions(false)
    .filter((option) => !existingCodes.has(option.code))
    .map((option) => ({
      label: option.reportingCurrency
        ? `${option.name} (${option.code}) - ${getI18n(
            'salesChannelCurrency.index.reportingcurrency',
          )}`
        : `${option.name} (${option.code})`,
      value: option.code,
    }));

  //other Available Currency List 展示核心位置
  const currencyConfigWithoutPrimary = useMemo(() => {
    return currencyConfig.filter(
      (item) => item?.primary === false && item?.deleted === false,
    );
  }, [currencyConfig]);

  //Edit自动sroll到指定currency setting
  const scrollToCurrencySectionByUuid = (uuid: string) => {
    const el = sectionRefs.current[uuid];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return view_currency_setting ? (
    <ConfigProvider
      getPopupContainer={(trigger) => {
        return trigger
          ? trigger.parentElement
          : document.getElementById('salesChannelCurrency');
      }}>
      <div className={styles.container} id='salesChannelCurrency'>
        <div className={styles.header}>
          <GoBackAndBreadCrumb
            goBack={navigateBack}
            breadcrumbData={breadcrumbData}
          />
          <TopRightBtnsGroups
            onSubmit={handleSubmit}
            onDiscard={handleDiscard}
            onSaveDisable={saveDisabled}
          />
        </div>
        <div className={styles.content}>
          <SectionWrapper
            className={styles.wrapper}
            title={
              <div className={styles.title}>
                <span>
                  {getI18n('salesChannelCurrency.index.salesChannelCurrency')}
                </span>
                <Popover title={null} content={<Hint />} placement={'rightTop'}>
                  <Icon type='icontooltip' className={styles.icon} />
                </Popover>
              </div>
            }>
            <div className={styles.warpperBody}>
              <ItemWrapper
                title={getI18n('salesChannelCurrency.index.primaryCurrency')}>
                <Select
                  labelInValue
                  className={styles.select}
                  size='large'
                  value={{
                    label: primaryCurrencyConfig?.currencyName,
                    value: primaryCurrencyConfig?.currencyCode,
                  }}
                  onChange={handleChangeCurrency}
                  disabled={!edit_currency_settings || nonEditable}
                  placeholder={getI18n(
                    'salesChannelCurrency.index.selectcurrency',
                  )}
                  options={currencyOptionExpectSelected}
                />
              </ItemWrapper>
              <Nav className={styles.wrapperNav} />
            </div>
          </SectionWrapper>
          <SectionWrapper
            className={styles.wrapper}
            title={getI18n(
              'salesChannelCurrency.index.secondaryCurrencyDisplay',
            )}>
            <div className={styles.warpperBody}>
              <ItemWrapper>
                <Checkbox
                  disabled={!edit_currency_settings}
                  checked={primaryCurrencyConfig?.showSecondary}
                  onChange={(e) => {
                    handleChangeShowSecond(e);
                  }}>
                  {getI18n(
                    'salesChannelCurrency.index.secondaryCurrencyDisplayCheckbox',
                  )}
                </Checkbox>
                {primaryCurrencyConfig?.showSecondary && (
                  <>
                    <Select
                      className={styles.seconarySelect}
                      labelInValue
                      onChange={handleChangeSecondCurrency}
                      placeholder={getI18n(
                        'salesChannelCurrency.index.selectcurrency',
                      )}
                      options={getOptions(false).map((c) => ({
                        label: c.reportingCurrency
                          ? `${c.name} (${c.code}) - ${getI18n(
                              'salesChannelCurrency.index.reportingcurrency',
                            )}`
                          : `${c.name} (${c.code})`,
                        value: c.code,
                      }))}
                      value={{
                        label: primaryCurrencyConfig?.secondaryCurrencyName,
                        value: primaryCurrencyConfig?.secondaryCurrencyCode,
                      }}
                      disabled={!edit_currency_settings}
                      size='large'
                      notFoundContent={
                        <span style={{ color: '#000' }}>
                          {getI18n('salesChannelCurrency.index.nosecondary')}
                        </span>
                      }
                    />
                    {primaryCurrencyConfig?.secondaryCurrencyCode && (
                      <CurrencyConfigSection
                        currencyConfig={primaryCurrencyConfig}
                        getI18n={getI18n}
                        decimalPositionOptions={decimalPositionOptions}
                        conversionOptions={conversionOptions}
                        formatExample={formatExample}
                        renderRoundingExample={renderRoundingExample}
                        renderTruncatingExample={renderTruncatingExample}
                        labelify={labelify}
                        secondCuSymbol={secondCuSymbol}
                        edit_currency_settings={edit_currency_settings}
                        handleChangeDecimalPosition={
                          handleChangeDecimalPosition
                        }
                        handleChangePropsViaEvent={handleChangePropsViaEvent}
                        handleChangePropsViaValue={handleChangePropsViaValue}
                        isReferenceCurrency={true}
                      />
                    )}
                  </>
                )}
              </ItemWrapper>
              <Nav className={styles.wrapperNav} />
            </div>
          </SectionWrapper>
          {!isMini && (
            <SectionWrapper
              className={styles.wrapper}
              title={getI18n(
                'salesChannelCurrency.index.otherAvailableCurrency',
              )}>
              <div className={styles.warpperBody}>
                <ItemWrapper>
                  <OtherAvailableCurrencyTable
                    currencyOptions={currencyOptionExpectSelected}
                    currencyConfig={currencyConfigWithoutPrimary}
                    handleOnAddOtherCurrency={handleOnAddOtherCurrency}
                    handleOnDeleteOtherCurrency={handleOnDeleteOtherCurrency}
                    disabled={!edit_currency_settings}
                    onEditCurrency={scrollToCurrencySectionByUuid}
                  />
                </ItemWrapper>
                <Nav className={styles.wrapperNav} />
              </div>
            </SectionWrapper>
          )}

          {[...(currencyConfig || [])]
            .filter((config) => config.deleted !== true)
            .sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1))
            .map((config) => (
              <div
                key={config.uuid}
                ref={(el) => {
                  sectionRefs.current[config.uuid] = el;
                }}>
                <SectionWrapper
                  key={config.uuid}
                  className={styles.wrapper}
                  containerClassName={styles.formatWrapperContainer}
                  title={`${getI18n(
                    'salesChannelCurrency.index.currencyFormat',
                  )} (${config.currencyCode})`}
                  subtitle={getI18n(
                    'salesChannelCurrency.index.currencyFormatSubtitle',
                  )}>
                  <CurrencyConfigSection
                    currencyConfig={config}
                    getI18n={getI18n}
                    decimalPositionOptions={decimalPositionOptions}
                    conversionOptions={conversionOptions}
                    formatExample={formatExample}
                    renderRoundingExample={renderRoundingExample}
                    renderTruncatingExample={renderTruncatingExample}
                    labelify={labelify}
                    secondCuSymbol={secondCuSymbol}
                    edit_currency_settings={edit_currency_settings}
                    handleChangeDecimalPosition={handleChangeDecimalPosition}
                    handleChangePropsViaEvent={handleChangePropsViaEvent}
                    handleChangePropsViaValue={handleChangePropsViaValue}
                  />
                </SectionWrapper>
              </div>
            ))}

          <RouteBlock
            onConfirm={(f) => f()}
            okText={getI18nFromGeneral('general.commonHeader.discard_text')}
            active={!saveDisabled}
            title={getI18nFromGeneral('general.commonHeader.discard_header')}
            content={getI18nFromGeneral('general.commonHeader.leave_content')}
          />
        </div>
      </div>
    </ConfigProvider>
  ) : (
    <NoPermission />
  );
}

export default SalesChannelModeAccessControlHOC(SalesChannelCurrency);

const Hint = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <div className={styles.hint}>
      <div className={styles.hintTitle}>
        {getI18n('salesChannelCurrency.index.salesChannelCurrency')}
      </div>
      <p className={styles.paragraph}>
        {getI18n('salesChannelCurrency.index.salesChannelCurrencyTip1')}
      </p>
      <p className={styles.paragraph}>
        {getI18n('salesChannelCurrency.index.salesChannelCurrencyTip2')}
      </p>
    </div>
  );
};

const Nav = ({ className }: { className: string }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <div className={classnames(styles.nav, className)}>
      <Icon type='iconinfo2' className={styles.info} />
      <span>
        {`${getI18n('salesChannelCurrency.index.gotostoreSettings')} >`}&nbsp;
      </span>
      <Link to={'/settings/currency'} className={styles.link}>
        {getI18n('salesChannelCurrency.index.currency')}
      </Link>
      <span>
        &nbsp;{getI18n('salesChannelCurrency.index.gotostoreSettings2')}
      </span>
    </div>
  );
};
